const toNumber = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

export const parseLatLong = (latLong) => {
  if (!latLong || typeof latLong !== 'string') return null
  const parts = latLong.split(',')
  if (parts.length !== 2) return null

  const lat = toNumber(parts[0].trim())
  const lng = toNumber(parts[1].trim())
  if (lat == null || lng == null) return null
  if (lat === 0 && lng === 0) return null

  return [lat, lng]
}

const toRadians = (degree) => degree * (Math.PI / 180)

const getDistanceInMeters = (pointA, pointB) => {
  const [lat1, lng1] = pointA
  const [lat2, lng2] = pointB

  const earthRadius = 6371000
  const dLat = toRadians(lat2 - lat1)
  const dLng = toRadians(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return earthRadius * c
}

const findNearestPointIndex = (routePoints, targetPoint) => {
  if (!routePoints.length || !targetPoint) return -1

  let nearestIndex = -1
  let minDistance = Number.POSITIVE_INFINITY

  routePoints.forEach((point, index) => {
    const distance = getDistanceInMeters(point.position, targetPoint)
    if (distance < minDistance) {
      minDistance = distance
      nearestIndex = index
    }
  })

  return nearestIndex
}

const normalizeRoutePoints = (routeData = []) => {
  const points = []

  routeData.forEach((item, index) => {
    const position = Array.isArray(item)
      ? [toNumber(item[0]), toNumber(item[1])]
      : parseLatLong(item && item.lat_long)

    if (!position || position[0] == null || position[1] == null) return

    const timestamp = toNumber(item && item.timestamp)
    points.push({
      index,
      timestamp,
      position
    })
  })

  return points
}

const buildSegmentFromTimeWindow = (routePoints, startTs, endTs) => {
  if (!routePoints.length) return []
  if (startTs == null || endTs == null) return []

  const fromTs = Math.min(startTs, endTs)
  const toTs = Math.max(startTs, endTs)

  return routePoints
    .filter((point) => point.timestamp != null && point.timestamp >= fromTs && point.timestamp <= toTs)
    .map((point) => point.position)
}

const buildSegmentFromNearestPoints = (routePoints, startPosition, endPosition) => {
  if (!routePoints.length || !startPosition || !endPosition) return []

  const startIndex = findNearestPointIndex(routePoints, startPosition)
  const endIndex = findNearestPointIndex(routePoints, endPosition)
  if (startIndex === -1 || endIndex === -1) return []

  const fromIndex = Math.min(startIndex, endIndex)
  const toIndex = Math.max(startIndex, endIndex)
  return routePoints.slice(fromIndex, toIndex + 1).map((point) => point.position)
}

const getRouteTimeWindow = (routePoints = []) => {
  const timestamps = (routePoints || [])
    .map((point) => point && point.timestamp)
    .filter((timestamp) => timestamp != null)

  if (!timestamps.length) return { startTs: null, endTs: null }
  return {
    startTs: Math.min(...timestamps),
    endTs: Math.max(...timestamps)
  }
}

const doesEventOverlapRouteWindow = (startTs, endTs, routeWindow) => {
  if (!routeWindow || routeWindow.startTs == null || routeWindow.endTs == null) return true

  const resolvedStart = startTs != null ? startTs : endTs
  const resolvedEnd = endTs != null ? endTs : startTs
  if (resolvedStart == null || resolvedEnd == null) return true

  const eventFrom = Math.min(resolvedStart, resolvedEnd)
  const eventTo = Math.max(resolvedStart, resolvedEnd)
  return eventTo >= routeWindow.startTs && eventFrom <= routeWindow.endTs
}

const getEventValue = (event, keys = []) => {
  if (!event || !keys || !keys.length) return null
  for (let i = 0; i < keys.length; i += 1) {
    const value = event[keys[i]]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }
  return null
}

const getEventPosition = (event, keys = []) => {
  if (!keys || !keys.length) return null
  for (let i = 0; i < keys.length; i += 1) {
    const parsed = parseLatLong(event && event[keys[i]])
    if (parsed) return parsed
  }
  return null
}

const getEventNumber = (event, keys = []) => {
  const value = getEventValue(event, keys)
  if (value == null) return null
  return toNumber(value)
}

export const buildRouteSegmentsFromEvents = (routeData = [], events = [], options = {}) => {
  if (!routeData.length || !events.length) return []

  const routePoints = normalizeRoutePoints(routeData)
  if (!routePoints.length) return []
  const routeWindow = getRouteTimeWindow(routePoints)

  const {
    keyPrefix = 'event',
    startLocationKeys = ['start_location'],
    endLocationKeys = ['end_location'],
    startTimestampKeys = ['start_timestamp'],
    endTimestampKeys = ['end_timestamp'],
    durationKeys = ['duration'],
    metricFields = []
  } = options

  const segments = []

  events.forEach((event, index) => {
    const startPosition = getEventPosition(event, startLocationKeys)
    const endPosition = getEventPosition(event, endLocationKeys)
    if (!startPosition && !endPosition) return

    const startTs = getEventNumber(event, startTimestampKeys)
    const endTs = getEventNumber(event, endTimestampKeys)
    const durationMs = getEventNumber(event, durationKeys)

    // Prevent false segments when current playback window excludes this event.
    if (!doesEventOverlapRouteWindow(startTs, endTs, routeWindow)) return

    let positions = buildSegmentFromTimeWindow(routePoints, startTs, endTs)

    if (positions.length < 2) {
      positions = buildSegmentFromNearestPoints(routePoints, startPosition, endPosition)
    }

    if (positions.length < 2 && startPosition && endPosition) {
      positions = [startPosition, endPosition]
    }

    if (positions.length >= 2) {
      const segment = {
        key: `${keyPrefix}-${index}-${startTs || 'na'}-${endTs || 'na'}`,
        positions,
        durationMs
      }

      metricFields.forEach((metric) => {
        if (!metric || !metric.outputKey) return
        segment[metric.outputKey] = getEventNumber(event, metric.keys || [])
      })

      segments.push(segment)
    }
  })

  return segments
}

export const buildOverspeedRouteSegments = (routeData = [], overspeedEvents = []) => (
  buildRouteSegmentsFromEvents(routeData, overspeedEvents, {
    keyPrefix: 'overspeed',
    startLocationKeys: ['start_location', 'start_lat_long', 'startLatLong'],
    endLocationKeys: ['end_location', 'end_lat_long', 'endLatLong'],
    startTimestampKeys: ['start_timestamp', 'start_time', 'startTs'],
    endTimestampKeys: ['end_timestamp', 'end_time', 'endTs'],
    durationKeys: ['duration', 'duration_ms'],
    metricFields: [
      { outputKey: 'avgSpeed', keys: ['average_speed', 'avg_speed'] },
      { outputKey: 'maxSpeed', keys: ['max_speed', 'peak_speed'] }
    ]
  })
)

export const buildFaultyHdopRouteSegments = (routeData = [], faultyHdopEvents = []) => (
  buildRouteSegmentsFromEvents(routeData, faultyHdopEvents, {
    keyPrefix: 'faulty-hdop',
    startLocationKeys: ['start_location', 'start_lat_long', 'startLatLong'],
    endLocationKeys: ['end_location', 'end_lat_long', 'endLatLong'],
    startTimestampKeys: ['start_timestamp', 'start_time', 'startTs'],
    endTimestampKeys: ['end_timestamp', 'end_time', 'endTs'],
    durationKeys: ['duration', 'duration_ms'],
    metricFields: [
      { outputKey: 'avgHdop', keys: ['average_hdop', 'avg_hdop', 'hdop_avg'] },
      { outputKey: 'maxHdop', keys: ['max_hdop', 'hdop_max'] }
    ]
  })
)
